import React from 'react';
import { PRICING } from '../pricing';
import TerminalIcon from '@mui/icons-material/Terminal';
import QuizIcon from '@mui/icons-material/Quiz';

export const dsa = {
    title: "Data Structures & Algorithms",
    subtitle: "Algorithm Mastery",
    badge: "RECOMMENDED",
    icon: <TerminalIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    description: "Master algorithmic patterns and data structure manipulation.",
    features: [
      "500+ Curated Problems",
      "Frame-by-Frame Visualization",
      "Optimal Space & Time Complexity",
      "Blind 75 & Grind 169 Covered"
    ],
    price: PRICING.dsa.price,
    mrp: PRICING.dsa.mrp,
    topics: [
      {
        id: "twoPointers",
        title: "Two Pointer",
        desc: "Utilize two pointers to iterate through an array or list from different ends or with different speeds.",
        badge: "Array/String",
        fileUrl: "/documents/dsa/two_pointer.md",
        group: "Linear",
        problems: [
          { id: "tp_1", title: "Linked List Cycle", badge: "Easy", fileUrl: "/documents/dsa/tp_ll_cycle.md" },
          { id: "tp_2", title: "Two Sum II", badge: "Medium", fileUrl: "/documents/dsa/tp_two_sum.md" },
          { id: "tp_3", title: "Container with Most Water", badge: "Medium", fileUrl: "/documents/dsa/tp_water.md" },
          { id: "tp_4", title: "3Sum", badge: "Medium", fileUrl: "/documents/dsa/tp_3sum.md" }
        ]
      },
      {
        id: "slidingWindow",
        title: "Sliding Window",
        desc: "Use two pointers or a deque to maintain a window of elements that satisfies a condition.",
        badge: "Array/String",
        fileUrl: "/documents/dsa/sliding_window.md",
        group: "Linear",
        problems: [
          { id: "sw_1", title: "Longest Substring Without Repeating", badge: "Medium", fileUrl: "/documents/dsa/sw_longest_sub.md" },
          { id: "sw_2", title: "Minimum Window Substring", badge: "Hard", fileUrl: "/documents/dsa/sw_min_window.md" },
          { id: "sw_3", title: "Sliding Window Maximum", badge: "Hard", fileUrl: "/documents/dsa/sw_maximum.md" }
        ]
      },
      {
        id: "modifiedBinarySearch",
        title: "Modified Binary Search",
        desc: "Adjust binary search to solve problems beyond simple searching, often in a rotated/unsorted array.",
        badge: "Search",
        fileUrl: "/documents/dsa/modified_bs.md",
        group: "Linear",
        problems: [
          { id: "bs_1", title: "Search in Rotated Sorted Array", badge: "Medium", fileUrl: "/documents/dsa/bs_rotated.md" },
          { id: "bs_2", title: "Find Peak Element", badge: "Medium", fileUrl: "/documents/dsa/bs_peak.md" },
          { id: "bs_3", title: "Search a 2D Matrix", badge: "Medium", fileUrl: "/documents/dsa/bs_matrix.md" }
        ]
      },
      {
        id: "monotonicStack",
        title: "Monotonic Stack",
        desc: "Use a stack to maintain elements in a specific monotonic order (increasing or decreasing).",
        badge: "Stack",
        fileUrl: "/documents/dsa/monotonic_stack.md",
        group: "Linear",
        problems: [
          { id: "ms_4", title: "Valid Parentheses", badge: "Easy", fileUrl: "/documents/dsa/stack_valid_parens.md" },
          { id: "ms_3", title: "Daily Temperatures", badge: "Medium", fileUrl: "/documents/dsa/ms_temperatures.md" },
          { id: "ms_1", title: "Largest Rectangle in Histogram", badge: "Hard", fileUrl: "/documents/dsa/ms_histogram.md" },
          { id: "ms_2", title: "Trapping Rain Water", badge: "Hard", fileUrl: "/documents/dsa/ms_rain_water.md" }
        ]
      },
      {
        id: "prefixSum",
        title: "Prefix Sum",
        desc: "Use a running total to calculate cumulative sums which can be used to quickly find subarray sums.",
        badge: "Math/Array",
        fileUrl: "/documents/dsa/prefix_sum.md",
        group: "Linear",
        problems: [
          { id: "pre_3", title: "Range Sum Query - Immutable", badge: "Easy", fileUrl: "/documents/dsa/pre_range.md" },
          { id: "pre_1", title: "Subarray Sum Equals K", badge: "Medium", fileUrl: "/documents/dsa/pre_sum_k.md" },
          { id: "pre_2", title: "Continuous Subarray Sum", badge: "Medium", fileUrl: "/documents/dsa/pre_continuous.md" }
        ]
      },
      {
        id: "bfs",
        title: "Binary Tree BFS",
        desc: "Use a queue to traverse the tree level by level.",
        badge: "Tree/Graph",
        fileUrl: "/documents/dsa/bfs.md",
        group: "Non-Linear",
        problems: [
          { id: "bfs_1", title: "Binary Tree Level Order Traversal", badge: "Medium", fileUrl: "/documents/dsa/bfs_level_order.md" },
          { id: "bfs_3", title: "Binary Tree Zigzag Level Order Traversal", badge: "Medium", fileUrl: "/documents/dsa/bfs_zigzag.md" }
        ]
      },
      {
        id: "dfs",
        title: "Binary Tree DFS",
        desc: "Use recursion or an explicit stack to traverse the tree pre-order, in-order, or post-order.",
        badge: "Tree",
        fileUrl: "/documents/dsa/dfs.md",
        group: "Non-Linear",
        problems: [
          { id: "bfs_2", title: "Minimum Depth of Binary Tree", badge: "Easy", fileUrl: "/documents/dsa/bfs_min_depth.md" },
          { id: "dfs_1", title: "Binary Tree Inorder Traversal", badge: "Easy", fileUrl: "/documents/tree_inorder.md" },
          { id: "dfs_2", title: "Path Sum", badge: "Easy", fileUrl: "/documents/dsa/dfs_path_sum.md" },
          { id: "dfs_3", title: "Maximum Depth of Binary Tree", badge: "Easy", fileUrl: "/documents/dsa/dfs_max_depth.md" },
          { id: "dfs_4", title: "Binary Tree Maximum Path Sum", badge: "Hard", fileUrl: "/documents/dsa/dfs_max_path_sum.md" }
        ]
      },

      {
        id: "topKElements",
        title: "Top K Elements",
        desc: "Use a heap or quickselect algorithm to find the top K elements.",
        badge: "Heap",
        fileUrl: "/documents/dsa/top_k.md",
        group: "Non-Linear",
        problems: [
          { id: "topk_1", title: "Kth Largest Element in an Array", badge: "Medium", fileUrl: "/documents/dsa/topk_kth_largest.md" },
          { id: "topk_2", title: "Top K Frequent Elements", badge: "Medium", fileUrl: "/documents/dsa/topk_frequent.md" },
          { id: "topk_3", title: "K Closest Points to Origin", badge: "Medium", fileUrl: "/documents/dsa/topk_closest.md" }
        ]
      },
      {
        id: "topologicalSort",
        title: "Topological Sort",
        desc: "Use DFS or Kahn’s algorithm to order tasks that depend on other tasks.",
        badge: "Graph",
        fileUrl: "/documents/dsa/topological_sort.md",
        group: "Non-Linear",
        problems: [
          { id: "topo_1", title: "Course Schedule", badge: "Medium", fileUrl: "/documents/dsa/topo_course_schedule.md" },
          { id: "topo_2", title: "Course Schedule II", badge: "Medium", fileUrl: "/documents/dsa/topo_course_schedule_2.md" },
          { id: "topo_3", title: "Alien Dictionary", badge: "Hard", fileUrl: "/documents/dsa/topo_alien_dict.md" }
        ]
      },
      {
        id: "subset",
        title: "Subset",
        desc: "Use backtracking or bit manipulation to generate all subsets.",
        badge: "Backtracking",
        fileUrl: "/documents/dsa/subset.md",
        group: "Non-Linear",
        problems: [
          { id: "sub_1", title: "Subsets", badge: "Medium", fileUrl: "/documents/dsa/sub_subsets.md" },
          { id: "sub_2", title: "Subsets II", badge: "Medium", fileUrl: "/documents/dsa/sub_subsets_2.md" },
          { id: "sub_3", title: "Combination Sum", badge: "Medium", fileUrl: "/documents/dsa/sub_combo_sum.md" },
          { id: "sub_4", title: "Permutations of a String", badge: "Medium", fileUrl: "/documents/dsa/sub_permutations.md" }
        ]
      },
      {
        id: "islandMatrix",
        title: "Island Matrix Traversal",
        desc: "Use DFS or BFS to traverse a grid, typically to count the number of islands or connected components.",
        badge: "Graph/Matrix",
        fileUrl: "/documents/dsa/island_matrix.md",
        group: "Non-Linear",
        problems: [
          { id: "im_1", title: "Number of Islands", badge: "Medium", fileUrl: "/documents/dsa/im_islands.md" },
          { id: "im_2", title: "Max Area of Island", badge: "Medium", fileUrl: "/documents/dsa/im_area.md" },
          { id: "im_3", title: "Surrounded Regions", badge: "Medium", fileUrl: "/documents/dsa/im_regions.md" }
        ]
      },
      {
        id: "unionFind",
        title: "Disjoint Set (Union Find)",
        desc: "Track elements partitioned into disjoint (non-overlapping) subsets. Near constant time for finding cycles.",
        badge: "Graphs",
        fileUrl: "/documents/dsa/uf_redundant.md",
        group: "Non-Linear",
        problems: [
          { id: "uf_1", title: "Redundant Connection", badge: "Medium", fileUrl: "/documents/dsa/uf_redundant.md" },
          { id: "uf_2", title: "Number of Connected Components", badge: "Medium", fileUrl: "/documents/dsa/uf_components.md" }
        ]
      },
      {
        id: "advancedGraphs",
        title: "Advanced Graphs",
        desc: "Graph algorithms mapping optimal pathways: Dijkstra's, Bellman-Ford, Prim's, and Kruskal's.",
        badge: "Graphs",
        fileUrl: "/documents/dsa/advgraph_network.md",
        group: "Non-Linear",
        problems: [
          { id: "advgraph_1", title: "Network Delay Time", badge: "Medium", fileUrl: "/documents/dsa/advgraph_network.md" },
          { id: "advgraph_2", title: "Cheapest Flights Within K Stops", badge: "Medium", fileUrl: "/documents/dsa/advgraph_flights.md" }
        ]
      },
      {
        id: "palindromicSubsequence",
        title: "Palindromic Subsequence",
        desc: "Use dynamic programming to find the longest palindromic subsequence within a string.",
        badge: "DP",
        fileUrl: "/documents/dsa/palindromic_subseq.md",
        group: "Linear",
        problems: [
          { id: "ps_1", title: "Longest Palindromic Subsequence", badge: "Medium", fileUrl: "/documents/dsa/ps_longest.md" },
          { id: "ps_2", title: "Count Different Palindromic Subsequences", badge: "Hard", fileUrl: "/documents/dsa/ps_count.md" }
        ]
      },
      {
        id: "longestCommonSubstring",
        title: "Longest Common Substring",
        desc: "Use dynamic programming or suffix arrays to find the longest substring common to two or more strings.",
        badge: "DP",
        fileUrl: "/documents/dsa/longest_common.md",
        group: "Linear",
        problems: [
          { id: "lcs_1", title: "Longest Common Substring", badge: "Medium", fileUrl: "/documents/dsa/lcs_substring.md" },
          { id: "lcs_2", title: "Longest Common Subsequence", badge: "Medium", fileUrl: "/documents/dsa/lcs_subsequence.md" }
        ]
      },
      {
        id: "linkedList",
        title: "Linked List",
        desc: "Manipulate node-based data structures by carefully reassigning pointers.",
        badge: "Pointers",
        fileUrl: "/documents/dsa/ll_lru_cache.md",
        group: "Linear",
        problems: [
          { id: "ll_1", title: "LRU Cache", badge: "Medium", fileUrl: "/documents/dsa/ll_lru_cache.md" },
          { id: "ll_2", title: "Merge K Sorted Lists", badge: "Hard", fileUrl: "/documents/dsa/ll_merge_k.md" },
          { id: "ll_3", title: "Reverse Nodes in k-Group", badge: "Hard", fileUrl: "/documents/dsa/ll_reverse_k.md" }
        ]
      },
      {
        id: "postfixSum",
        title: "Postfix Sum",
        desc: "Similar to prefix sum, but accumulate sums from the end of the array to the beginning.",
        badge: "Math/Array",
        fileUrl: "/documents/dsa/postfix_sum.md",
        group: "Linear",
        problems: [
          { id: "post_2", title: "Minimum Number of Operations to Move All Balls", badge: "Medium", fileUrl: "/documents/dsa/post_balls.md" },
          { id: "post_1", title: "Product of Array Except Self", badge: "Medium", fileUrl: "/documents/dsa/post_product.md" }
        ]
      },
      {
        id: "greedy",
        title: "Greedy Algorithm",
        desc: "Make a series of choices, each of which looks best at the moment, aiming for optimal global.",
        badge: "Greedy",
        fileUrl: "/documents/dsa/greedy.md",
        group: "Linear",
        problems: [
          { id: "gr_1", title: "Interval Scheduling Maximization", badge: "Medium", fileUrl: "/documents/dsa/gr_interval.md" },
          { id: "gr_2", title: "Jump Game", badge: "Medium", fileUrl: "/documents/dsa/gr_jump.md" },
          { id: "gr_3", title: "Coin Change (minimum coins)", badge: "Medium", fileUrl: "/documents/dsa/gr_coin.md" },
          { id: "gr_4", title: "Minimum Cost to Connect Sticks", badge: "Medium", fileUrl: "/documents/dsa/gr_sticks.md" }
        ]
      },
      {
        id: "mergeIntervals",
        title: "Merge Intervals",
        desc: "Sort intervals by start time, then sweep to merge overlapping ranges in a single pass.",
        badge: "Array/Sort",
        fileUrl: "/documents/dsa/merge_intervals.md",
        group: "Linear",
        problems: [
          { id: "mi_1", title: "Merge Intervals", badge: "Medium", fileUrl: "/documents/dsa/mi_merge.md" },
          { id: "mi_2", title: "Insert Interval", badge: "Medium", fileUrl: "/documents/dsa/mi_insert.md" },
          { id: "mi_3", title: "Non-overlapping Intervals", badge: "Medium", fileUrl: "/documents/dsa/mi_nonoverlap.md" }
        ]
      },
      {
        id: "knapsack",
        title: "0/1 Knapsack",
        desc: "Classic DP pattern: for each item, choose to include or exclude it to optimize a target under a constraint.",
        badge: "DP",
        fileUrl: "/documents/dsa/knapsack_01.md",
        group: "Linear",
        problems: [
          { id: "ks_1", title: "Partition Equal Subset Sum", badge: "Medium", fileUrl: "/documents/dsa/ks_partition.md" },
          { id: "ks_2", title: "Target Sum", badge: "Medium", fileUrl: "/documents/dsa/ks_target_sum.md" },
          { id: "ks_3", title: "Last Stone Weight II", badge: "Medium", fileUrl: "/documents/dsa/ks_last_stone.md" }
        ]
      },
      {
        id: "dp1d",
        title: "1D Dynamic Programming",
        desc: "Solve problems by breaking them down into simpler subproblems linearly.",
        badge: "DP",
        fileUrl: "/documents/dsa/dp1d_house_robber.md",
        group: "Linear",
        problems: [
          { id: "dp1d_1", title: "House Robber", badge: "Medium", fileUrl: "/documents/dsa/dp1d_house_robber.md" },
          { id: "dp1d_2", title: "Longest Increasing Subsequence", badge: "Medium", fileUrl: "/documents/dsa/dp1d_lis.md" },
          { id: "dp1d_3", title: "Decode Ways", badge: "Medium", fileUrl: "/documents/dsa/dp1d_decode.md" }
        ]
      },
      {
        id: "trie",
        title: "Trie (Prefix Tree)",
        desc: "A tree where each node is a character; shared prefixes share nodes. Enables O(L) insert, search, and prefix queries.",
        badge: "Tree/String",
        fileUrl: "/documents/dsa/trie.md",
        group: "Non-Linear",
        problems: [
          { id: "trie_1", title: "Implement Trie", badge: "Medium", fileUrl: "/documents/dsa/trie_implement.md" },
          { id: "trie_3", title: "Word Break", badge: "Medium", fileUrl: "/documents/dsa/trie_word_break.md" },
          { id: "trie_2", title: "Word Search II", badge: "Hard", fileUrl: "/documents/dsa/trie_word_search.md" }
        ]
      },
      {
        id: "mathGeo",
        title: "Math & Geometry",
        desc: "Number theory, bit manipulation, and spatial coordinate grids to solve mathematical constraints.",
        badge: "Math/Logic",
        fileUrl: "/documents/dsa/math_rotate.md",
        group: "Non-Linear",
        problems: [
          { id: "math_1", title: "Rotate Image", badge: "Medium", fileUrl: "/documents/dsa/math_rotate.md" },
          { id: "math_2", title: "Spiral Matrix", badge: "Medium", fileUrl: "/documents/dsa/math_spiral.md" },
          { id: "math_3", title: "Set Matrix Zeroes", badge: "Medium", fileUrl: "/documents/dsa/math_zeroes.md" }
        ]
      },
      {
        id: "cyclicSort",
        title: "Cyclic Sort",
        desc: "Sort an array of numbers from 1 to N in O(N) time and O(1) space by swapping elements to their correct indices.",
        badge: "Array/Sort",
        fileUrl: "/documents/dsa/cyclic_missing.md",
        group: "Linear",
        problems: [
          { id: "cyc_1", title: "Missing Number", badge: "Easy", fileUrl: "/documents/dsa/cyclic_missing.md" },
          { id: "cyc_2", title: "Find All Duplicates in an Array", badge: "Medium", fileUrl: "/documents/dsa/cyclic_duplicates.md" },
          { id: "cyc_3", title: "First Missing Positive", badge: "Hard", fileUrl: "/documents/dsa/cyclic_first_missing.md" }
        ]
      },
      {
        id: "dsa_practice_problems",
        title: "Essential Coding Problems",
        desc: "Must-know coding problems across mixed topics.",
        badge: "Practice",
        group: "Practice",
        icon: <QuizIcon />,
        problems: [
          { id: "prac_valid_anagram", title: "Valid Anagram", badge: "Arrays", fileUrl: "/documents/dsa/dsa_prac_valid_anagram.md" },
          { id: "prac_contains_dup", title: "Contains Duplicate", badge: "Arrays", fileUrl: "/documents/dsa/dsa_prac_contains_duplicate.md" },
          { id: "prac_best_stock", title: "Best Time to Buy and Sell Stock", badge: "Window", fileUrl: "/documents/dsa/dsa_prac_best_time_stock.md" },
          { id: "prac_lca_bst", title: "LCA of a BST", badge: "Trees", fileUrl: "/documents/dsa/dsa_prac_lca_bst.md" },
          { id: "prac_climbing_stairs", title: "Climbing Stairs", badge: "DP", fileUrl: "/documents/dsa/dsa_prac_climbing_stairs.md" },
          { id: "prac_reverse_ll", title: "Reverse Linked List", badge: "Linked List", fileUrl: "/documents/dsa/dsa_prac_reverse_ll.md" },
          { id: "prac_min_stack", title: "Min Stack", badge: "Stacks", fileUrl: "/documents/dsa/dsa_prac_min_stack.md" },
          { id: "prac_merge_lists", title: "Merge Two Sorted Lists", badge: "Pointers", fileUrl: "/documents/dsa/dsa_prac_merge_lists.md" },
          { id: "prac_hamming", title: "Number of 1 Bits", badge: "Bitwise", fileUrl: "/documents/dsa/dsa_prac_hamming_weight.md" },
          { id: "prac_max_subarray", title: "Maximum Subarray", badge: "Greedy/DP", fileUrl: "/documents/dsa/dsa_prac_max_subarray.md" },
          { id: "prac_array_rotation_right", title: "Right Rotate Array", badge: "Arrays", fileUrl: "/documents/dsa/dsa_prac_array_rotation_right.md" },
          { id: "prac_array_rotation_left", title: "Left Rotate Array", badge: "Arrays", fileUrl: "/documents/dsa/dsa_prac_array_rotation_left.md" },
          { id: "prac_reverse_string", title: "Reverse String", badge: "Strings", fileUrl: "/documents/dsa/dsa_prac_reverse_string.md" },
          { id: "prac_longest_common_prefix", title: "Longest Common Prefix", badge: "Strings", fileUrl: "/documents/dsa/dsa_prac_longest_common_prefix.md" },
          { id: "prac_atoi", title: "String to Integer (atoi)", badge: "Strings", fileUrl: "/documents/dsa/dsa_prac_atoi.md" },
          { id: "prac_isomorphic_strings", title: "Isomorphic Strings", badge: "Strings", fileUrl: "/documents/dsa/dsa_prac_isomorphic_strings.md" },
          { id: "prac_ocean_view", title: "Find Buildings With an Ocean View", badge: "Arrays", fileUrl: "/documents/dsa/dsa_prac_ocean_view.md" }
        ]
      }
    ]
  };
